import { type ChangeEvent, useCallback, useMemo } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod/v4';
import { useIxcSweepsV1SweepPlanQueryServiceSweepPlanGet } from '@interxfi/api-client/generated/hooks/ixc-sweeps-v1-sweep-plan-query-service-sweep-plan-get';
import { useProgramAccess } from '@interxfi/api-client/program/program-auth-access-selector';
import SweepsIcon from '@interxfi/design-system/icons/react/icon-sweeps';
import {
  type IxcPortfolioV1SweepsControls,
  type IxcPortfolioV1SweepsControlsSettings,
  IxcSweepsV1SweepPlanStatus,
} from '@interxfi/types/generated/api';
import { AccordionFormGroup } from '../../../../../../components/card-group/form/accordion-form-group';
import {
  buildSweepPlanComboboxQuery,
  SweepPlanComboboxWithLens,
  SweepPlanMultiComboboxWithLens,
} from '../../../../../../components/data-combobox-input/entity-config/sweep-plan';
import { AppSwitchInputWithLens } from '../../../../../../components/form/app-switch-input';
import { addDefinedValues } from '../../../../../../util/has-value';

export const programControlsSweepsFormSchema = z.object({
  canSweepAccounts: z.object({
    value: z.boolean(),
    defaultSweepPlanId: z.string(),
    eligibleSweepPlanIds: z.array(z.string()),
  }),
});

export type ProgramControlsSweepsFormValues = z.infer<typeof programControlsSweepsFormSchema>;

export const programControlsSweepsFormDefaultValues: ProgramControlsSweepsFormValues = {
  canSweepAccounts: {
    value: false,
    defaultSweepPlanId: '',
    eligibleSweepPlanIds: [],
  },
};

export function getInitialFormValuesForProgramSweepsControls(data: IxcPortfolioV1SweepsControls | undefined): ProgramControlsSweepsFormValues {
  if (!data) {
    return programControlsSweepsFormDefaultValues;
  }

  return {
    canSweepAccounts: {
      value: Boolean(data?.settings?.canSweepAccounts),
      defaultSweepPlanId: data?.settings?.defaultSweepPlanId || '',
      eligibleSweepPlanIds: data?.settings?.eligibleSweepPlanIds || [],
    },
  };
}

export function formValuesToProgramSweepsControls(values: ProgramControlsSweepsFormValues): IxcPortfolioV1SweepsControls {
  const settings = addDefinedValues<IxcPortfolioV1SweepsControlsSettings>(
    {
      canSweepAccounts: values.canSweepAccounts.value,
    },
    {
      defaultSweepPlanId: (values.canSweepAccounts.value ? values.canSweepAccounts.defaultSweepPlanId : undefined) || undefined,
      eligibleSweepPlanIds:
        (values.canSweepAccounts.value && values.canSweepAccounts.eligibleSweepPlanIds.length
          ? values.canSweepAccounts.eligibleSweepPlanIds
          : undefined) || undefined,
    },
  );

  return addDefinedValues<IxcPortfolioV1SweepsControls>(
    {},
    {
      settings: Object.values(settings).filter(Boolean).length > 0 ? settings : undefined,
    },
  );
}

const eligibleSweepPlansQuery = buildSweepPlanComboboxQuery({ status: IxcSweepsV1SweepPlanStatus.Active });

interface ProgramSweepsControlsFormProps {
  lens: Lens<ProgramControlsSweepsFormValues>;
  programId: string | undefined;
}

export function ProgramControlsSweepsForm({ lens, programId }: ProgramSweepsControlsFormProps) {
  const programOrClearingAccess = useProgramAccess(programId, true);
  const { setValue } = useFormContext();
  const { name: fieldName } = lens.interop();
  const canSweepAccounts = useWatch(lens.focus('canSweepAccounts.value').interop());
  const handleToggleCanSweep = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.checked) {
        setValue(fieldName, programControlsSweepsFormDefaultValues, { shouldValidate: true });
      }
    },
    [fieldName, setValue],
  );

  const eligibleSweepPlanIds = useWatch({ ...lens.focus('canSweepAccounts.eligibleSweepPlanIds').interop(), defaultValue: [] });
  const defaultSweepPlanQuery = useMemo(
    () =>
      eligibleSweepPlanIds?.length
        ? buildSweepPlanComboboxQuery({ status: IxcSweepsV1SweepPlanStatus.Active, sweepPlanId: eligibleSweepPlanIds })
        : undefined,
    [eligibleSweepPlanIds],
  );

  const defaultSweepPlanId = useWatch(lens.focus('canSweepAccounts.defaultSweepPlanId').interop());

  // Ensure the default sweep plan is included in the eligible sweep plans query, so it can be selected as default if sweeps are enabled and a default is set
  const { data: defaultSweepPlanData } = useIxcSweepsV1SweepPlanQueryServiceSweepPlanGet(
    programOrClearingAccess && defaultSweepPlanId ? { sweepPlanId: defaultSweepPlanId } : undefined,
    undefined,
    programOrClearingAccess,
  );
  const ensuredData = useMemo(
    () => (defaultSweepPlanData?.sweepPlan ? [defaultSweepPlanData.sweepPlan] : undefined),
    [defaultSweepPlanData?.sweepPlan],
  );

  return (
    <AccordionFormGroup icon={<SweepsIcon aria-hidden />} title="Sweeps">
      <AccordionFormGroup.Section
        toggleInputElement={
          <AppSwitchInputWithLens
            align="space-between"
            label="Allow Account Sweeps"
            lens={lens.focus('canSweepAccounts.value')}
            onChange={handleToggleCanSweep}
          />
        }
      >
        {canSweepAccounts ? (
          <>
            <SweepPlanMultiComboboxWithLens
              ensureData={ensuredData}
              label="Eligible Sweep Plans"
              lens={lens.focus('canSweepAccounts.eligibleSweepPlanIds')}
              queryOptions={programOrClearingAccess ? eligibleSweepPlansQuery : null}
              withAccess={programOrClearingAccess}
            />

            <SweepPlanComboboxWithLens
              ensureData={ensuredData}
              label="Default Sweep Plan"
              lens={lens.focus('canSweepAccounts.defaultSweepPlanId')}
              queryOptions={programOrClearingAccess ? defaultSweepPlanQuery : null}
              withAccess={programOrClearingAccess}
            />
          </>
        ) : null}
      </AccordionFormGroup.Section>
    </AccordionFormGroup>
  );
}
